/**
 * Download a canvas element as a PNG file.
 */
export function downloadPng(canvas: HTMLCanvasElement, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/**
 * Download text content as a text file.
 */
export function downloadTextFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Wait for the next animation frame.
 * Useful for ensuring React has re-rendered before capturing.
 */
export function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Delay for a specified number of milliseconds.
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Accept types for `showSaveFilePicker` when saving PNG exports. */
export const PNG_SAVE_ACCEPT: FilePickerAcceptType[] = [
  { description: 'PNG image', accept: { 'image/png': ['.png'] } },
];

/** Accept types for `showSaveFilePicker` when saving SVG exports. */
export const SVG_SAVE_ACCEPT: FilePickerAcceptType[] = [
  { description: 'SVG image', accept: { 'image/svg+xml': ['.svg'] } },
];

export type SaveTargetResult =
  | { kind: 'aborted' }
  | { kind: 'legacy'; suggestedFilename: string }
  | { kind: 'handle'; handle: FileSystemFileHandle };

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

/**
 * When the File System Access API is available, opens the system save dialog immediately
 * so cancel does not run after expensive export work, and `AbortError` maps to `aborted`.
 * Otherwise returns `legacy` (anchor download — save vs cancel cannot be detected).
 */
export async function pickSaveTarget(
  suggestedFilename: string,
  acceptTypes: FilePickerAcceptType[],
): Promise<SaveTargetResult> {
  if (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'showSaveFilePicker' in window &&
    typeof window.showSaveFilePicker === 'function'
  ) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: suggestedFilename,
        types: acceptTypes,
      });
      return { kind: 'handle', handle };
    } catch (e) {
      if (isAbortError(e)) {
        return { kind: 'aborted' };
      }
      throw e;
    }
  }
  return { kind: 'legacy', suggestedFilename };
}

/** Writes `blob` to the target from `pickSaveTarget`. Returns false only for `aborted` (caller should not reach). */
export async function commitBlobToSaveTarget(target: SaveTargetResult, blob: Blob): Promise<boolean> {
  if (target.kind === 'aborted') {
    return false;
  }
  if (target.kind === 'handle') {
    const writable = await target.handle.createWritable();
    try {
      await writable.write(blob);
    } finally {
      await writable.close();
    }
    return true;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = target.suggestedFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}

/**
 * Download SVG content as an SVG file.
 */
export function downloadSvgFile(svgContent: string, filename: string): void {
  const blob = new Blob([svgContent], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Escape special XML characters for use in SVG attributes.
 */
export function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

import { getPTSansBoldBase64, generateFontFaceRule } from './fontEmbed';
import { REACTION_SVG_TEXT_FONT_FAMILY } from '../constants/previewTypography';
import {
  getFittedSceneTransform,
  getMoleculeNaturalLayout,
  getReactionNaturalLayout,
  MOLECULE_LAYOUT_BASE,
  REACTION_LAYOUT_BASE,
} from './exportLayout';

interface GenerateSvgOptions {
  exportElement: HTMLElement;
  topText: string;
  bottomText: string;
  arrowType?: 'single' | 'double';
  templateMode?: 'reaction' | 'molecule';
  exportWidth?: number;
  exportHeight?: number;
  reactantZoom?: number;
  schemeDisplayZoom?: number;
}

/**
 * Generate SVG content from the export element.
 * Combines molecule SVGs and arrow into a single SVG with embedded font.
 * Accessibility metadata (<title>, <desc>, aria-label) is added separately
 * by embedSVGMetadata after generation.
 */
export async function generateSvgContent({
  exportElement,
  topText,
  bottomText,
  arrowType = 'single',
  templateMode = 'reaction',
  exportWidth,
  exportHeight,
  reactantZoom = 1,
  schemeDisplayZoom: _schemeDisplayZoom = 1,
}: GenerateSvgOptions): Promise<string> {
  // Load and encode the font
  const fontBase64 = await getPTSansBoldBase64();
  if (!fontBase64) {
    console.warn(
      '[downloadUtils] SVG export without embedded PTSans-Bold; labels may use system fallback fonts.',
    );
  }
  const fontFaceRule = generateFontFaceRule(fontBase64);
  const svgs = exportElement.querySelectorAll('svg');
  const serializer = new XMLSerializer();

  // Get actual arrow dimensions from the SVG (index 1 is the arrow)
  const arrowSvg = svgs[1] as SVGSVGElement;
  const arrowWidth = arrowSvg ? parseFloat(arrowSvg.getAttribute('width') || '48') : 48;
  const arrowHeight = arrowSvg ? parseFloat(arrowSvg.getAttribute('height') || '32') : 32;

  // Get text span elements to measure their actual positions
  const spans = exportElement.querySelectorAll('span');
  const exportRect = exportElement.getBoundingClientRect();
  /** Layout size of the export root (logical export pixels, ignores preview-only ancestor scale). */
  const layoutW = exportElement.offsetWidth || exportWidth || 1;
  const layoutH = exportElement.offsetHeight || exportHeight || 1;
  /**
   * Preview wraps the export node in `transform: scale(canvasScale)`, so getBoundingClientRect is in
   * viewport pixels while exportWidth/Height are layout pixels. Map measured geometry to export space.
   */
  const toExportX = (viewportPx: number) =>
    exportRect.width > 0 ? viewportPx * (layoutW / exportRect.width) : viewportPx;
  const toExportY = (viewportPx: number) =>
    exportRect.height > 0 ? viewportPx * (layoutH / exportRect.height) : viewportPx;

  // Find top and bottom text spans by their content
  let topTextSpan: HTMLSpanElement | null = null;
  let bottomTextSpan: HTMLSpanElement | null = null;

  spans.forEach(span => {
    if (span.textContent === topText) {
      topTextSpan = span;
    } else if (span.textContent === bottomText) {
      bottomTextSpan = span;
    }
  });

  // Preview DOM already uses layout-sized arrow SVG and label `rem`/`px` (scheme zoom in attributes,
  // not a separate CSS row scale). Do not multiply `schemeDisplayZoom` again on measured sizes.
  const scaledArrowWidth = arrowWidth;
  const scaledArrowHeight = arrowHeight;
  const scaledReactantZoom = reactantZoom;

  const reactionNatural = getReactionNaturalLayout(scaledArrowWidth, scaledReactantZoom, REACTION_LAYOUT_BASE);
  const moleculeNatural = getMoleculeNaturalLayout(MOLECULE_LAYOUT_BASE);
  const naturalWidth = templateMode === 'molecule' ? moleculeNatural.naturalWidth : reactionNatural.naturalWidth;
  const naturalHeight = templateMode === 'molecule' ? moleculeNatural.naturalHeight : reactionNatural.naturalHeight;
  const totalWidth = exportWidth ?? naturalWidth;
  const totalHeight = exportHeight ?? naturalHeight;
  const { scale, offsetX, offsetY } = getFittedSceneTransform(
    { width: totalWidth, height: totalHeight },
    naturalWidth,
    naturalHeight
  );

  // Start building the combined SVG with embedded font (omit empty @font-face — a family name
  // with no `src` causes inconsistent fallback across viewers, especially after failed fetch).
  const fontStyleBlock = fontFaceRule.trim()
    ? `<style type="text/css">
${fontFaceRule}
</style>`
    : '';
  // Note: No role="img" on outer SVG to allow screen reader navigation into foreignObject children
  let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}">
<defs>
${fontStyleBlock}</defs>
<rect width="100%" height="100%" fill="white"/>`;

  const scaledPadding = REACTION_LAYOUT_BASE.padding;
  const scaledGap = REACTION_LAYOUT_BASE.gap;
  const scaledMoleculeWidth =
    exportWidth && Number.isFinite(arrowWidth)
      ? Math.max(
          64,
          (exportWidth - scaledPadding * 2 - scaledGap * 2 - scaledArrowWidth) / 2
        )
      : REACTION_LAYOUT_BASE.moleculeWidth;
  const scaledMoleculeHeight = exportHeight
    ? Math.max(64, exportHeight - scaledPadding * 2)
    : REACTION_LAYOUT_BASE.moleculeHeight;

  // Approximate arrow X for fallback text placement when span lookup fails.
  const arrowX =
    offsetX +
    (scaledPadding +
      scaledMoleculeWidth +
      scaledGap) *
      scale;

  // Label font sizes track reactant zoom / arrow cluster scale (matches preview)
  let svgFontSizeTop = 16 * 1.1 * scaledReactantZoom;
  let svgFontSizeBottom = 16 * 1.1 * scaledReactantZoom;

  if (topTextSpan) {
    const fs = parseFloat(window.getComputedStyle(topTextSpan).fontSize);
    if (Number.isFinite(fs)) svgFontSizeTop = fs;
  }
  if (bottomTextSpan) {
    const fs = parseFloat(window.getComputedStyle(bottomTextSpan).fontSize);
    if (Number.isFinite(fs)) svgFontSizeBottom = fs;
  }

  const topBaselineOffset = svgFontSizeTop * 0.75;
  const bottomBaselineOffset = svgFontSizeBottom * 0.75;

  // Arrow section height matches preview: 2×1rem + arrow (14px at scale 1)
  const arrowSectionHeight = 32 * scaledReactantZoom + 14 * scaledReactantZoom;
  const arrowSectionTop =
    offsetY +
    (scaledPadding +
      (scaledMoleculeHeight - arrowSectionHeight) / 2) *
      scale;

  const topRowPx = 16 * scaledReactantZoom;

  // Default positions based on flex layout structure
  let topTextX = arrowX + (scaledArrowWidth * scale) / 2; // Default to center
  let topTextY = arrowSectionTop + topBaselineOffset * scale; // Top text baseline
  let bottomTextX = arrowX + (scaledArrowWidth * scale) / 2; // Default to center
  let bottomTextY =
    arrowSectionTop + (topRowPx + scaledArrowHeight + bottomBaselineOffset) * scale;

  // If we have the actual text spans, use their measured positions
  if (topTextSpan) {
    const topRect = topTextSpan.getBoundingClientRect();

    // Get computed style to measure actual text (excluding padding)
    const topStyle = window.getComputedStyle(topTextSpan);
    const topPaddingRight = parseFloat(topStyle.paddingRight) || 0;

    // Calculate center X: use the actual text width (excluding padding)
    // The padding shifts the text left, so we need to account for it
    const topActualTextWidth = topRect.width - topPaddingRight;
    topTextX = toExportX(topRect.left - exportRect.left + topActualTextWidth / 2);

    // Calculate baseline Y: top of element + baseline offset
    topTextY = toExportY(topRect.top - exportRect.top) + topBaselineOffset;
  }

  if (bottomTextSpan) {
    const bottomRect = bottomTextSpan.getBoundingClientRect();

    // Get computed style to measure actual text (excluding padding)
    const bottomStyle = window.getComputedStyle(bottomTextSpan);
    const bottomPaddingRight = parseFloat(bottomStyle.paddingRight) || 0;

    // Calculate center X: use the actual text width (excluding padding)
    const bottomActualTextWidth = bottomRect.width - bottomPaddingRight;
    bottomTextX = toExportX(bottomRect.left - exportRect.left + bottomActualTextWidth / 2);

    // Calculate baseline Y: top of element + baseline offset
    bottomTextY = toExportY(bottomRect.top - exportRect.top) + bottomBaselineOffset;
  }

  // Add top text (reagents)
  if (topText && templateMode !== 'molecule') {
    svgContent += `
<text x="${topTextX}" y="${topTextY}" text-anchor="middle" font-family="${REACTION_SVG_TEXT_FONT_FAMILY}" font-size="${svgFontSizeTop}" fill="#000000">${escapeXml(topText)}</text>`;
  }

  // Add bottom text (conditions)
  if (bottomText && templateMode !== 'molecule') {
    svgContent += `
<text x="${bottomTextX}" y="${bottomTextY}" text-anchor="middle" font-family="${REACTION_SVG_TEXT_FONT_FAMILY}" font-size="${svgFontSizeBottom}" fill="#000000">${escapeXml(bottomText)}</text>`;
  }

  // Process each SVG element
  // DOM order: reactant SVG (index 0), arrow SVG (index 1), product SVG (index 2)
  svgs.forEach((svg, index) => {
    // Clone the SVG to avoid modifying the original
    const clonedSvg = svg.cloneNode(true) as SVGElement;

    // Remove any existing width/height attributes that might conflict
    clonedSvg.removeAttribute('width');
    clonedSvg.removeAttribute('height');

    // Serialize the inner content
    const svgString = serializer.serializeToString(clonedSvg);

    // Extract inner content (remove outer <svg> tags)
    const innerContent = svgString
      .replace(/<svg[^>]*>/, '')
      .replace(/<\/svg>$/, '');

    // Use measured DOM geometry (layout + viewBox; AtomScaleWrap may still apply CSS scale)
    // to keep SVG export visually identical to the preview.
    // DOM order: reactant (0), arrow (1), product (2)
    if (templateMode === 'molecule' && index !== 0) return;
    if (templateMode !== 'molecule' && index > 2) return;

    const rect = (svg as SVGGraphicsElement).getBoundingClientRect();
    const x = toExportX(rect.left - exportRect.left);
    const y = toExportY(rect.top - exportRect.top);
    const width = toExportX(rect.width);
    const height = toExportY(rect.height);

    // Get the viewBox from original or use defaults
    const viewBox = svg.getAttribute('viewBox') || `0 0 ${width} ${height}`;

    // Preserve stroke and fill attributes from the original SVG (important for arrow)
    const stroke = svg.getAttribute('stroke');
    const strokeWidth = svg.getAttribute('stroke-width');
    const fill = svg.getAttribute('fill');
    const strokeLinecap = svg.getAttribute('stroke-linecap');
    const strokeLinejoin = svg.getAttribute('stroke-linejoin');
    const preserveAspectRatio = svg.getAttribute('preserveAspectRatio');

    // Preserve accessibility attributes (aria-label, role) for screen reader support
    const ariaLabel = svg.getAttribute('aria-label');
    const role = svg.getAttribute('role');

    // Build additional attributes string
    let additionalAttrs = '';
    if (stroke) additionalAttrs += ` stroke="${stroke}"`;
    if (strokeWidth) additionalAttrs += ` stroke-width="${strokeWidth}"`;
    if (fill) additionalAttrs += ` fill="${fill}"`;
    if (strokeLinecap) additionalAttrs += ` stroke-linecap="${strokeLinecap}"`;
    if (strokeLinejoin) additionalAttrs += ` stroke-linejoin="${strokeLinejoin}"`;
    if (preserveAspectRatio) additionalAttrs += ` preserveAspectRatio="${preserveAspectRatio}"`;
    if (ariaLabel) additionalAttrs += ` aria-label="${escapeXml(ariaLabel)}"`;
    if (role) additionalAttrs += ` role="${role}"`;

    svgContent += `
<svg x="${x}" y="${y}" width="${width}" height="${height}" viewBox="${viewBox}"${additionalAttrs}>
${innerContent}
</svg>`;
  });

  svgContent += '\n</svg>';

  return svgContent;
}
